/**
 * Redux reducer for authentication state, including login, logout, permissions, and notifications.
 *
 * @module redux/authentication/reducer
 * @author Snowfly
 * @version 1.0.0
 */
import {
    LOGIN_FAIL,
    LOGIN_REQUEST,
    LOGIN_SUCCESS,
    CHECK_AUTHORIZATION,
    USER_LOGOUT,
    FETCH_FAILURE,
    SAVE_PERMISSIONS,
    UPDATE_LOGIN_STATUS,
    REGISTRATION_FAILURE,
    UPDATE_ROLE,
    RESET_NOTIFICATION,
    FETCH_NOTIFICATIONS
} from "./authTypes";
import {clearAudioStorage} from "../../utils/audioFetch";

/**
 * Initial state for the authentication reducer.
 *
 * @type {Object}
 * @property {boolean} isAuth - Whether the current user is authenticated.
 * @property {Object} user - Authenticated user profile data.
 * @property {Array} permissions - List of permission objects assigned to the user.
 * @property {boolean} isLoading - Whether an async operation is in progress.
 * @property {string} errMessage - Error message from the last failed login attempt.
 * @property {string|null} status - Last HTTP status or error message.
 * @property {boolean|null} loginStatus - Result of the last login attempt.
 * @property {Array} data - Generic data payload from the last fetch.
 * @property {boolean|null} registrationStatus - Result of the last registration attempt.
 * @property {string} registrationMessage - Message from the last registration response.
 * @property {Array} notifications - List of user notifications.
 * @property {number} newNotifications - Count of unread notifications.
 * @property {Object} company - Company data associated with the authenticated user.
 * @property {Array} notifList - Detailed notification list with metadata.
 * @property {Array} products - Products available to the user's company.
 * @property {Array} callTypes - Available call type options.
 * @property {Array} outsourcers - Outsourcer options available to the user.
 */
const initialState = {
    isAuth: false,
    user: {},
    permissions: [],
    isLoading: false,
    errMessage: "",
    status: null,
    loginStatus: null,
    data: [],
    registrationStatus: null,
    registrationMessage: "",
    notifications: [],
    newNotifications: 0,
    company: {},
    notifList: [],
    products: [],
    callTypes: [],
    outsourcers: [],
};

/**
 * A reducer function to manage the application state based on different action types.
 * Handles authentication, user information, notifications, permissions, and various other state updates.
 *
 * @param {Object} state - The current state of the application.
 * @param {Object} [state.isLoading] - Indicates whether an API call or some asynchronous operation is in progress.
 * @param {boolean} [state.isAuth] - Indicates the authentication status of the user.
 * @param {Object} [state.user] - Contains detailed information about the authenticated user.
 * @param {Object} [state.company] - Details of the company associated with the user.
 * @param {Array} [state.permissions] - List of permissions assigned to the user.
 * @param {Array} [state.notifications] - List of notifications received by the user.
 * @param {Array} [state.notifList] - Detailed list of notifications including additional metadata.
 * @param {number} [state.newNotifications] - The count of new/unread notifications.
 * @param {Array} [state.products] - List of products related to the organization or user.
 * @param {Array} [state.callTypes] - Array of available call types.
 * @param {Array} [state.outsourcers] - Information about outsourcing providers.
 * @param {boolean} [state.loginStatus] - Represents the success or failure of the login process.
 * @param {boolean} [state.permissionDenied] - Indicates if the user has been denied permissions.
 * @param {string} [state.errMessage] - Holds error messages from failed operations.
 * @param {string} [state.registrationMessage] - Stores message related to registration operations.
 * @param {boolean} [state.registrationStatus] - Indicates the result of registration actions.
 * @param {string} [state.status] - Represents the status of the asynchronous operation (e.g., HTTP status code).
 * @param {Object} action - An action that triggers state changes in the reducer.
 * @param {string} [action.type] - The type of action that determines the logic to execute.
 * @param {Object} [action.payload] - Additional data sent along with the action to update the state.
 *
 * @returns {Object} The updated state based on the action type.
 *
 * Action types handled:
 * - `LOGIN_REQUEST`: Updates loading state to `true` when login process starts.
 * - `LOGIN_SUCCESS`: Handles a successful login, stores user and token details in `localStorage` and updates state.
 * - `LOGIN_FAIL`: Updates state with error message and sets authentication to `false` upon login failure.
 * - `CHECK_AUTHORIZATION`: Checks `localStorage` for authentication tokens and sets user state accordingly.
 * - `USER_LOGOUT`: Clears `localStorage` and updates state to reflect logout.
 * - `FETCH_FAILURE`: Handles fetch operation failures and redirects based on HTTP status codes (e.g., `401` or `403`).
 * - `SAVE_PERMISSIONS`: Updates user permissions and notification counts in both `localStorage` and state.
 * - `UPDATE_LOGIN_STATUS`: Updates login and registration status in the state.
 * - `REGISTRATION_FAILURE`: Handles registration failure by storing messages and registration status.
 * - `UPDATE_ROLE`: Updates the user's role in the state.
 * - `FETCH_NOTIFICATIONS`: Updates the notification state, including the count of new/unread notifications.
 * - `RESET_NOTIFICATION`: Resets the count of new notifications to `0`.
 * - `default`: Returns the current state if no matching action type is found.
 */
const reducer = (state = initialState, action = {}) => {
    const {type, payload} = action;
    switch (type) {
        case LOGIN_REQUEST:
            return {
                ...state,
                isLoading: true,
            };

        case LOGIN_SUCCESS:
            localStorage.setItem('token', payload.token);
            localStorage.setItem('user', JSON.stringify(payload.user));
            localStorage.setItem('cd', JSON.stringify({
                company: payload.company,
                permissions: payload.permissions,
                notifications: payload.notifications,
                notifList: payload.notifList,
                new_notifications: payload.new_notifications,
                products: payload.products,
                callTypes: payload.callTypes,
                outsourcers: payload.outsourcers,
            }));

            if (payload.login_to_agent_account) {
                window.location.href = '/dashboard';
            }
            return {
                ...state,
                isAuth: true,
                isLoading: false,
                user: payload.user,
                company: payload.company,
                permissions: payload.permissions,
                notifications: payload.notifications,
                notifList: payload.notifList,
                new_notifications: payload.new_notifications,
                products: payload.products,
                callTypes: payload.callTypes,
                outsourcers: payload.outsourcers,
                loginStatus: true,
            };

        case LOGIN_FAIL:
            return {
                ...state,
                isLoading: false,
                isAuth: false,
                errMessage: payload.message,
                loginStatus: payload.success,
            };

        case CHECK_AUTHORIZATION:
            if (
                !JSON.parse(localStorage.getItem('user')) ||
                !localStorage.getItem('token')
            ) {
                return {
                    ...state,
                    isAuth: false,
                };
            }
            return {
                ...state,
                isAuth: true,
                user: JSON.parse(localStorage.getItem('user')),
            };

        case USER_LOGOUT:
            clearAudioStorage().then(() => {
                localStorage.clear();
            });
            return {
                ...state,
                isAuth: false,
            };

        case FETCH_FAILURE:
            return (() => {
                const status = (payload.message ?? "").split(' '),
                    statusCode = parseInt(
                        status[status.length - 1] ?? "0"
                    );
                if (403 === statusCode) {
                    localStorage.setItem('redirectUrl', '/permissiondenied');
                    window.location.href = '/permissiondenied';
                    return {
                        ...state,
                        isLoading: false,
                        permissionDenied: true,
                    };
                }
                if (401 === statusCode) {
                    window.location.href = '/login';
                    localStorage.clear('token');
                    localStorage.clear('user');
                    return {
                        ...state,
                        isLoading: false,
                        isAuth: false,
                        data: payload.data,
                        status: payload,
                    };
                }
                return {
                    ...state,
                    isLoading: false,
                    data: payload.data,
                    status: status[status.length - 1],
                };
            })();

        case SAVE_PERMISSIONS:
            return (() => {
                let user = JSON.parse(localStorage.getItem('user'));
                user.permissions[0].permission_details = [
                    ...payload.permissions[0].permission_details
                ];
                localStorage.setItem('user', JSON.stringify(user));

                return {
                    ...state,
                    user: user,
                    permissions: payload.permissions,
                    newNotifications: 0+!!payload.new_notifications,
                };
            })();

        case UPDATE_LOGIN_STATUS:
            return {
                ...state,
                loginStatus: payload,
                registrationStatus: payload,
            };
        case REGISTRATION_FAILURE:
            return {
                ...state,
                registrationMessage: payload.message ? payload.message : '',
                registrationStatus: payload.success,
            };

        case UPDATE_ROLE:
            return {
                ...state,
                user: {
                    ...state.user,
                    role: payload,
                },
            };

        case FETCH_NOTIFICATIONS:
            return (() => {
                let newIds = action.payload.data.notifList.filter(
                    (item) => item.isNew
                );

                return {
                    ...state,
                    isLoading: true,
                    notifications: action.payload.data.notifList,
                    newNotifications: newIds.length,
                };
            })();

        case RESET_NOTIFICATION:
            return {
                ...state,
                newNotifications: 0,
            };

        default:
            return state;
    }
};

export default reducer;
